import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "../../assets/css/aiChat.css";
import {
  addUserMessage,
  clearChat,
  toggleChat,
  truncateFromMessage,
} from "../../redux/ai/aiSlice";
import {
  fetchAiStatus,
  fetchAiRecent,
  sendAiMessage,
} from "../../redux/ai/aiActions";
import FormattedMessage from "./FormattedMessage";
import TaskCards from "./TaskCards";
import ReportView from "./ReportView";
import {
  selectAiIsOpen,
  selectAiMessages,
  selectAiProvider,
  selectAiStatus,
  selectAiRecent,
} from "../../redux/ai/aiSelectors";


const SUGGESTIONS = [
  "What are my pending tasks?",
  "Which of my tasks are most urgent?",
  "How do I update a development task status?",
  "Give me tips to improve my productivity",
];

// Header lightbulb dropdown — grouped quick prompts.
const SUGGESTION_GROUPS = [
  {
    title: "TEAM REPORTS",
    items: [
      { icon: "bi-people-fill", text: "Overall team report" },
      { icon: "bi-calendar-day", text: "Today's team report" },
      { icon: "bi-calendar-minus", text: "Yesterday's team report" },
      { icon: "bi-calendar-range", text: "Team report for this month" },
    ],
  },
  {
    title: "MANAGEMENT REPORTS",
    items: [
      { icon: "bi-clipboard-data", text: "MD report" },
      { icon: "bi-grid-1x2", text: "Give me an overview" },
      { icon: "bi-calendar-plus", text: "Today's new queries by employee" },
      { icon: "bi-list-ul", text: "All pending queries (team-wide)" },
      { icon: "bi-people", text: "Client-wise report for last 3 months" },
      { icon: "bi-arrow-repeat", text: "Which menu is repeated and who handled it last?" },
      { icon: "bi-bar-chart", text: "All employee performance this month" },
      { icon: "bi-speedometer", text: "Team efficiency for last 3 months" },
      { icon: "bi-hourglass-split", text: "Which tasks took more than the estimated time and who?" },
      { icon: "bi-flag", text: "Priority-wise task alignment" },
    ],
  },
  {
    title: "TASK INSIGHTS",
    items: [
      { icon: "bi-list-task", text: "What are my pending tasks?" },
      { icon: "bi-speedometer2", text: "How is my performance today?" },
      { icon: "bi-clock-history", text: "Show my tasks pending more than 2 days" },
      { icon: "bi-bug", text: "How many bugs are assigned to me?" },
    ],
  },
  {
    title: "QUICK ACTIONS",
    items: [
      { icon: "bi-graph-up", text: "Summarize my workload" },
      { icon: "bi-question-circle", text: "How do I update a task status?" },
    ],
  },
];

// Small badge showing which model actually answered.
const providerBadge = (provider) => {
  if (provider === "gpt") return { label: "GPT", cls: "ai-badge-gpt" };
  if (provider === "gemini") return { label: "Gemini", cls: "ai-badge-gemini" };
  if (provider === "data") return { label: "Task data", cls: "ai-badge-data" };
  return null;
};

const AiChatWidget = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(selectAiIsOpen);
  const messages = useSelector(selectAiMessages);
  const provider = useSelector(selectAiProvider);
  const status = useSelector(selectAiStatus);
  const recentFromHistory = useSelector(selectAiRecent);

  const [input, setInput] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [sqlOpenId, setSqlOpenId] = useState(null);
  // Size mode: 0 = normal floating, 1 = phone full screen (narrow + full height),
  // 2 = full screen (wide + full height). The header button cycles 0 → 1 → 2 → 0.
  const [sizeMode, setSizeMode] = useState(0);
  const [showMenu, setShowMenu] = useState(false);

  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  const isLoading = status === "loading";

  // RECENT = the user's last asked questions, from tbl_chat_history (limit 4).
  // Falls back to the current in-session messages if history isn't loaded yet.
  const recentQuestions = (() => {
    if (Array.isArray(recentFromHistory) && recentFromHistory.length) {
      return recentFromHistory.slice(0, 4);
    }
    const seen = new Set();
    const out = [];
    for (let i = messages.length - 1; i >= 0 && out.length < 4; i--) {
      const msg = messages[i];
      if (msg.role !== "user") continue;
      const c = (msg.content || "").trim();
      if (!c || seen.has(c.toLowerCase())) continue;
      seen.add(c.toLowerCase());
      out.push(c);
    }
    return out;
  })();

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchAiStatus());
      dispatch(fetchAiRecent());
    }
  }, [isOpen, dispatch]);

  // Auto-scroll to the latest message / typing indicator.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading, isOpen]);

  const resetTextareaHeight = () => {
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };

  const handleSend = (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || isLoading) return;

    // When editing, drop the edited message + everything after it, then resend.
    let baseMessages = messages;
    if (editingId != null) {
      const idx = messages.findIndex((m) => m.id === editingId);
      if (idx !== -1) baseMessages = messages.slice(0, idx);
      dispatch(truncateFromMessage(editingId));
    }

    const history = baseMessages
      .filter((m) => !m.isError)
      .map((m) => ({ role: m.role, content: m.content }));

    dispatch(addUserMessage(trimmed));
    dispatch(
      sendAiMessage({ message: trimmed, provider, history, useTaskContext: true }),
    ).then(() => dispatch(fetchAiRecent())); // refresh RECENT from history

    setInput("");
    setEditingId(null);
    setShowMenu(false);
    resetTextareaHeight();
  };

  const handleEdit = (m) => {
    setEditingId(m.id);
    setInput(m.content);
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        120,
      )}px`;
    }
  };

  const handleCopy = (m) => {
    const text = m.content || "";
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
    setCopiedId(m.id);
    setTimeout(() => setCopiedId((id) => (id === m.id ? null : id)), 1500);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === "Escape" && editingId != null) {
      setEditingId(null);
      setInput("");
      resetTextareaHeight();
    }
  };

  const handleInput = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
  };

  return (
    <>
      {/* Floating launcher button */}
      {!isOpen && (
        <button
          className="ai-fab"
          title="Ask AI Assistant"
          onClick={() => dispatch(toggleChat(true))}
        >
          <i className="bi bi-robot"></i>
          <span className="ai-fab-pulse"></span>
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div
          className={`ai-chat-panel ${
            sizeMode === 1 ? "size-phone" : sizeMode === 2 ? "size-full" : ""
          }`}
        >
          {/* Header */}
          <div className="ai-chat-header">
            <div className="ai-chat-title">
              <span className="ai-chat-avatar">
                <i className="bi bi-robot"></i>
              </span>
              <div>
                <div className="ai-chat-name">AI Assistant</div>
                <div className="ai-chat-sub">SKY RP TaskManage</div>
              </div>
            </div>
            <div className="ai-chat-header-actions">
              {/* Suggestions dropdown */}
              <div className="ai-menu-wrap">
                <button
                  className={`ai-icon-btn ${showMenu ? "active" : ""}`}
                  title="Suggested questions"
                  onClick={() => setShowMenu((v) => !v)}
                >
                  <i className="bi bi-lightbulb"></i>
                </button>
                {showMenu && (
                  <>
                    <div
                      className="ai-menu-overlay"
                      onClick={() => setShowMenu(false)}
                    />
                    <div className="ai-menu">
                      {recentQuestions.length > 0 && (
                        <div className="ai-menu-group">
                          <div className="ai-menu-title">RECENT</div>
                          {recentQuestions.map((q) => (
                            <button
                              key={`recent-${q}`}
                              className="ai-menu-item"
                              onClick={() => handleSend(q)}
                            >
                              <i className="bi bi-clock-history"></i>
                              <span>{q}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {SUGGESTION_GROUPS.map((g) => (
                        <div key={g.title} className="ai-menu-group">
                          <div className="ai-menu-title">{g.title}</div>
                          {g.items.map((it) => (
                            <button
                              key={it.text}
                              className="ai-menu-item"
                              onClick={() => handleSend(it.text)}
                            >
                              <i className={`bi ${it.icon}`}></i>
                              <span>{it.text}</span>
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <button
                className="ai-icon-btn"
                title={
                  sizeMode === 0
                    ? "Phone full screen"
                    : sizeMode === 1
                    ? "Full screen"
                    : "Minimize"
                }
                onClick={() => setSizeMode((v) => (v + 1) % 3)}
              >
                <i
                  className={`bi ${
                    sizeMode === 0
                      ? "bi-phone"
                      : sizeMode === 1
                      ? "bi-arrows-fullscreen"
                      : "bi-fullscreen-exit"
                  }`}
                ></i>
              </button>
              <button
                className="ai-icon-btn"
                title="Clear conversation"
                onClick={() => {
                  dispatch(clearChat());
                  setEditingId(null);
                  setInput("");
                }}
              >
                <i className="bi bi-trash3"></i>
              </button>
              <button
                className="ai-icon-btn"
                title="Close"
                onClick={() => dispatch(toggleChat(false))}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="ai-chat-messages" ref={scrollRef}>
            {messages.length === 0 && (
              <div className="ai-empty">
                <div className="ai-empty-icon">
                  <i className="bi bi-chat-dots"></i>
                </div>
                <p className="ai-empty-title">How can I help you today?</p>
                <p className="ai-empty-text">
                  Ask about your tasks, working hours and reports, or anything
                  about using the app.
                </p>
                <div className="ai-suggestions">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      className="ai-suggestion"
                      onClick={() => handleSend(s)}
                    >
                      <i className="bi bi-stars ai-suggestion-ic"></i>
                      <span>{s}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m) => {
              const badge =
                m.role === "assistant" && !m.isError
                  ? providerBadge(m.provider)
                  : null;
              const isAssistant = m.role === "assistant";
              return (
                <div
                  key={m.id}
                  className={`ai-msg ai-msg-${m.role} ${
                    m.isError ? "ai-msg-error" : ""
                  }`}
                >
                  {isAssistant && (
                    <span className="ai-msg-avatar">
                      <i
                        className={`bi ${
                          m.isError ? "bi-exclamation-triangle" : "bi-robot"
                        }`}
                      ></i>
                    </span>
                  )}
                  <div className="ai-msg-col">
                    <div className="ai-msg-bubble">
                      <div className="ai-msg-text">
                        {isAssistant && !m.isError ? (
                          <>
                            <FormattedMessage content={m.content} />
                            {Array.isArray(m.tasks) && m.tasks.length > 0 && (
                              <TaskCards tasks={m.tasks} overview={m.overview} />
                            )}
                            {m.report && <ReportView report={m.report} />}
                          </>
                        ) : (
                          m.content
                        )}
                      </div>
                      {badge && (
                        <span className={`ai-badge ${badge.cls}`}>
                          {badge.label}
                          {m.groundedOnData && m.provider !== "data"
                            ? " · data"
                            : ""}
                        </span>
                      )}
                    </div>

                    {/* Per-message actions */}
                    {!m.isError && (
                      <div className="ai-msg-actions">
                        <button
                          className="ai-msg-act"
                          title={copiedId === m.id ? "Copied!" : "Copy"}
                          onClick={() => handleCopy(m)}
                        >
                          <i
                            className={`bi ${
                              copiedId === m.id ? "bi-check2" : "bi-clipboard"
                            }`}
                          ></i>
                        </button>
                        {m.role === "user" && (
                          <button
                            className="ai-msg-act"
                            title="Edit & resend"
                            onClick={() => handleEdit(m)}
                            disabled={isLoading}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                        )}
                        {m.sql && (
                          <button
                            className={`ai-msg-act ${sqlOpenId === m.id ? "active" : ""}`}
                            title="View SQL"
                            onClick={() =>
                              setSqlOpenId((id) => (id === m.id ? null : m.id))
                            }
                          >
                            <i className="bi bi-database"></i>
                          </button>
                        )}
                      </div>
                    )}

                    {m.sql && sqlOpenId === m.id && (
                      <div className="ai-sql">
                        <div className="ai-sql-head">
                          <span>
                            <i className="bi bi-database"></i> Executed SQL
                          </span>
                          <button
                            className="ai-sql-copy"
                            title="Copy SQL"
                            onClick={() => {
                              if (navigator.clipboard?.writeText)
                                navigator.clipboard.writeText(m.sql).catch(() => {});
                            }}
                          >
                            <i className="bi bi-clipboard"></i>
                          </button>
                        </div>
                        <pre>{m.sql}</pre>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {isLoading && (
              <div className="ai-msg ai-msg-assistant">
                <span className="ai-msg-avatar">
                  <i className="bi bi-robot"></i>
                </span>
                <div className="ai-msg-bubble">
                  <div className="ai-typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="ai-chat-input">
            {editingId != null && (
              <div className="ai-editing-hint">
                <i className="bi bi-pencil-square"></i> Editing message — press
                Esc to cancel
              </div>
            )}
            <div className="ai-chat-input-row">
              <textarea
                ref={textareaRef}
                rows={1}
                value={input}
                placeholder={
                  editingId != null ? "Edit your message…" : "Type your message…"
                }
                onChange={handleInput}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
              />
              <button
                className="ai-send-btn"
                onClick={() => handleSend()}
                disabled={isLoading || !input.trim()}
                title={editingId != null ? "Resend" : "Send"}
              >
                <i className="bi bi-send-fill"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AiChatWidget;
